import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ShieldLogo } from "../components/landing/Logo";
import {
  BarChart3, Clock, ChefHat, Eye, Phone, CheckCircle2,
  TrendingUp, DollarSign, AlertCircle, ArrowLeft, Crown,
  FileText, Camera, MessageSquare, Sparkles, Loader2,
  ArrowRight, Target, Award
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subtext, color = "gold", trend }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-[#0A1244] rounded-xl border border-gold/20 p-5"
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
      {trend && (
        <span className={`text-xs px-2 py-1 rounded-full ${
          trend > 0 ? "bg-lime/10 text-lime" : "bg-slate-700 text-slate-400"
        }`}>
          {trend > 0 ? "+" : ""}{trend}%
        </span>
      )}
    </div>
    <p className="text-2xl font-inter font-black text-white mb-1">{value}</p>
    <p className="text-sm text-slate-400">{label}</p>
    {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
  </motion.div>
);

// Mission Progress Component
const MissionProgress = ({ progress }) => {
  const missions = [
    { key: "welcome_completed", label: "Welcome Tour", icon: Sparkles },
    { key: "echo_tested", label: "Echo Call", icon: Phone },
    { key: "apollo_tested", label: "Apollo Vision", icon: Eye },
    { key: "athena_tested", label: "Athena SOP", icon: ChefHat },
    { key: "dashboard_viewed", label: "Analytics", icon: BarChart3 }
  ];

  return (
    <div className="space-y-3">
      {missions.map((mission, idx) => {
        const isCompleted = progress?.[mission.key];
        const Icon = mission.icon;
        return (
          <div key={mission.key} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isCompleted ? "bg-lime/20" : "bg-slate-700/50"
            }`}>
              {isCompleted ? (
                <CheckCircle2 className="w-4 h-4 text-lime" />
              ) : (
                <span className="text-xs text-slate-500">{idx + 1}</span>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${isCompleted ? "text-lime" : "text-slate-500"}`} />
                <span className={`text-sm ${isCompleted ? "text-white" : "text-slate-400"}`}>
                  {mission.label}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Recommendation Card Component
const RecommendationCard = ({ recommendation }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    className="flex items-start gap-3 p-4 rounded-lg bg-gold/5 border border-gold/20"
  >
    <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
      <span className="text-xs font-bold text-gold">{recommendation.priority}</span>
    </div>
    <div className="flex-1">
      <p className="text-sm font-medium text-white">{recommendation.action}</p>
      <p className="text-xs text-slate-400 mt-1">{recommendation.reason}</p>
    </div>
    <ArrowRight className="w-4 h-4 text-gold shrink-0" />
  </motion.div>
);

// SOP List Item
const SOPItem = ({ sop }) => (
  <div className="flex items-center justify-between p-3 rounded-lg bg-indigo-700/20 border border-indigo-500/20">
    <div className="flex items-center gap-3">
      <ChefHat className="w-4 h-4 text-orange-400" />
      <div>
        <p className="text-sm text-white">{sop.name}</p>
        <p className="text-xs text-slate-500">{sop.department}</p>
      </div>
    </div>
    <span className="text-sm font-medium text-gold">
      ${sop.estimated_cost?.toFixed(2) || "0.00"}
    </span>
  </div>
);

export default function TrialAnalytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const trialToken = sessionStorage.getItem("argus_trial_token");

  useEffect(() => {
    if (!trialToken) {
      navigate("/trial/login");
      return;
    }
    fetchAnalytics();
  }, [trialToken]);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/trial/analytics`, {
        headers: { Authorization: `Bearer ${trialToken}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        sessionStorage.removeItem("argus_trial_token");
        navigate("/trial/login");
      } else {
        setError(err.response?.data?.detail || "Failed to load analytics");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBecomeFounder = () => {
    navigate("/trial/checkout");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#040F49] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#040F49] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400">{error}</p>
          <Button onClick={() => navigate("/trial/dashboard")} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const progressPercent = (analytics?.missions_completed || 0) / 5 * 100;

  return (
    <div className="min-h-screen bg-[#040F49]">
      {/* Header */}
      <header className="border-b border-gold/10 bg-[#040F49]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/trial/dashboard"
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Dashboard</span>
            </Link>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/10 border border-gold/30">
                <Clock className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">
                  {analytics?.days_remaining || 0} days left
                </span>
              </div>
              
              <Button
                onClick={handleBecomeFounder}
                className="bg-gold hover:bg-gold/90 text-indigo-700 font-bold"
                data-testid="become-founder-analytics-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Become a Founder
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-8 h-8 text-gold" />
            <h1 className="text-2xl font-inter font-black text-white">
              Trial Analytics
            </h1>
          </div>
          <p className="text-slate-400">
            Your activity summary for {analytics?.restaurant_name || "your restaurant"}
          </p>
        </motion.div>

        {/* Engagement Score Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-r from-gold/20 to-lime/10 rounded-2xl border border-gold/30 p-6 mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Your Engagement Score</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-inter font-black text-gold">
                  {analytics?.engagement_score || 0}
                </span>
                <span className="text-lg text-slate-500">/100</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Based on missions, SOPs created, and feature usage
              </p>
            </div>
            <div className="w-24 h-24 rounded-full border-4 border-gold flex items-center justify-center bg-[#040F49]">
              <Award className={`w-10 h-10 ${
                analytics?.engagement_score >= 75 ? "text-lime" :
                analytics?.engagement_score >= 50 ? "text-gold" :
                "text-slate-500"
              }`} />
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Clock}
            label="Days Active"
            value={`${analytics?.days_active || 0}/5`}
            color="gold"
          />
          <StatCard
            icon={FileText}
            label="SOPs Created"
            value={analytics?.sops_created || 0}
            subtext="Voice recipes documented"
            color="orange"
          />
          <StatCard
            icon={Camera}
            label="Vision Analyses"
            value={analytics?.vision_analyses || 0}
            subtext={`${analytics?.vision_alerts_caught || 0} alerts caught`}
            color="blue"
          />
          <StatCard
            icon={DollarSign}
            label="Est. Monthly Savings"
            value={`$${(analytics?.potential_monthly_savings || 0).toLocaleString()}`}
            subtext="Based on your profile"
            color="lime"
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Progress & Recommendations */}
          <div className="lg:col-span-2 space-y-6">
            {/* Onboarding Progress */}
            <div className="bg-[#0A1244] rounded-xl border border-gold/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-inter font-bold text-white">
                  Onboarding Progress
                </h2>
                <span className="text-sm text-gold font-medium">
                  {analytics?.missions_completed || 0}/5 Complete
                </span>
              </div>
              
              <Progress value={progressPercent} className="h-3 mb-6" />
              
              <MissionProgress progress={analytics?.onboarding_progress} />
            </div>

            {/* Recommendations */}
            {analytics?.recommended_next_steps?.length > 0 && (
              <div className="bg-[#0A1244] rounded-xl border border-gold/20 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-gold" />
                  <h2 className="text-lg font-inter font-bold text-white">
                    Recommended Next Steps
                  </h2>
                </div>
                
                <div className="space-y-3">
                  {analytics.recommended_next_steps.map((rec, idx) => (
                    <RecommendationCard key={idx} recommendation={rec} />
                  ))}
                </div>
              </div>
            )}

            {/* SOPs Created */}
            {analytics?.sops_list?.length > 0 && (
              <div className="bg-[#0A1244] rounded-xl border border-gold/20 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ChefHat className="w-5 h-5 text-orange-400" />
                    <h2 className="text-lg font-inter font-bold text-white">
                      Your SOPs
                    </h2>
                  </div>
                  <span className="text-sm text-slate-400">
                    Total: ${analytics.total_sop_cost_documented?.toFixed(2) || "0.00"}
                  </span>
                </div>
                
                <div className="space-y-2">
                  {analytics.sops_list.slice(0, 5).map((sop, idx) => (
                    <SOPItem key={idx} sop={sop} />
                  ))}
                </div>
                
                {analytics.sops_list.length > 5 && (
                  <Link
                    to="/dashboard/sop-builder?trial=true"
                    className="block text-center text-sm text-gold hover:underline mt-4"
                  >
                    View all {analytics.sops_list.length} SOPs →
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Savings & CTA */}
          <div className="space-y-6">
            {/* Savings Summary */}
            <div className="bg-gradient-to-br from-[#0A1244] to-[#060D3A] rounded-xl border border-gold/30 p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-lime" />
                <h3 className="font-inter font-bold text-white">Your ROI Potential</h3>
              </div>
              
              <div className="space-y-4">
                <div className="text-center p-4 rounded-lg bg-gold/10">
                  <p className="text-xs text-slate-400 mb-1">Monthly Savings</p>
                  <p className="text-3xl font-inter font-black text-gold">
                    ${(analytics?.potential_monthly_savings || 0).toLocaleString()}
                  </p>
                </div>
                
                <div className="text-center p-4 rounded-lg bg-lime/10">
                  <p className="text-xs text-slate-400 mb-1">Annual Savings</p>
                  <p className="text-3xl font-inter font-black text-lime">
                    ${((analytics?.potential_monthly_savings || 0) * 12).toLocaleString()}
                  </p>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Compatibility Score</span>
                  <span className="text-lime font-bold">
                    {analytics?.compatibility_score || 0}%
                  </span>
                </div>
              </div>
            </div>

            {/* Founder CTA */}
            <div className="bg-gold/10 rounded-xl border border-gold/40 p-6 text-center">
              <Crown className="w-8 h-8 text-gold mx-auto mb-3" />
              <h3 className="font-inter font-bold text-white mb-2">
                Ready to Go Full-Time?
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                Lock in <span className="text-gold font-bold">$374/month</span> for life and keep all your trial data.
              </p>
              <Button
                onClick={handleBecomeFounder}
                className="w-full bg-gold hover:bg-gold/90 text-indigo-700 font-bold"
              >
                Become a Founding Member
              </Button>
            </div>

            {/* Trial Stats */}
            <div className="bg-[#0A1244] rounded-xl border border-slate-700 p-5">
              <h3 className="text-sm font-medium text-slate-400 mb-3">Trial Activity</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Started</span>
                  <span className="text-white">
                    {new Date(analytics?.trial_start).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Expires</span>
                  <span className={analytics?.is_expired ? "text-red-400" : "text-white"}>
                    {new Date(analytics?.trial_end).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Chat Sessions</span>
                  <span className="text-white">{analytics?.chat_sessions || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Features Tested</span>
                  <span className="text-white">{analytics?.features_tested || 0}/4</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
